import { useEffect, useState } from "react";
import {
  CartesianGrid,
  Cell,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Point } from "@/lib/types";
import { cn } from "@/lib/utils";

export interface ScatterDataset {
  name: string;
  color: string;
  points: Point[];
}

type ScatterChartCardProps = {
  title: string;
  horizontalLabel?: string;
  verticalLabel?: string;
  // 'auto' means legend is visible if the visualization has more than one dataset
  showLegend?: true | false | "auto";
  showOriginAxis?: boolean;
  datasets: ScatterDataset[];
  unit?: string;
  loading?: boolean;
  children?: React.ReactNode;
} & React.ComponentProps<typeof Card>;

const getAbsMax = (points: Point[]) => {
  return points.reduce(
    (max, point) => {
      const newMax = { ...max };
      if (Math.abs(point.x) > max.x) {
        newMax.x = Math.abs(point.x);
      }

      if (Math.abs(point.y) > max.y) {
        newMax.y = Math.abs(point.y);
      }

      return newMax;
    },
    { x: -Infinity, y: -Infinity },
  );
};

export const ScatterChartCard = ({
  title,
  horizontalLabel = "Horizontal",
  verticalLabel = "Vertical",
  datasets = [],
  showLegend = "auto",
  showOriginAxis = true,
  unit = "in",
  loading = false,
  children,
  ...cardProps
}: ScatterChartCardProps) => {
  const [isLegendVisible, setLegendVisible] = useState(false);

  const [xDomain, setXDomain] = useState([0, 0]);
  const [yDomain, setYDomain] = useState([0, 0]);
  const [data, setData] = useState<Point[]>([]);
  const [colors, setColors] = useState<string[]>([]);

  // This is used to render some empty chart when there is no data
  useEffect(() => {
    const hasData = datasets.some((dataset) => dataset.points.length > 0);

    if (!hasData) {
      // Use default domains when there's no data
      setXDomain([-10, 10]);
      setYDomain([-10, 10]);
      setData([]);
      setColors([]);
      return;
    }

    const { x: maxAbsX, y: maxAbsY } = getAbsMax(
      datasets.map((dataset) => {
        return getAbsMax(dataset.points);
      }),
    );

    setData(
      datasets.reduce<Point[]>((acc, current: ScatterDataset) => {
        return acc.concat(current.points);
      }, []),
    );

    setColors(
      datasets.reduce((acc: string[], current: ScatterDataset) => {
        return acc.concat(Array(current.points.length).fill(current.color));
      }, []),
    );

    // add a 20% margin to avoid points touching the edges
    // rounding to make the ticks of the chart nicer at the borders
    setXDomain([Math.round(maxAbsX * -1.2), Math.round(maxAbsX * 1.2)]);
    setYDomain([Math.round(maxAbsY * -1.2), Math.round(maxAbsY * 1.2)]);
  }, [datasets]);

  useEffect(() => {
    setLegendVisible(
      showLegend === true || (showLegend === "auto" && datasets.length > 1),
    );
  }, [showLegend, datasets.length]);

  return (
    <Card className="w-full max-w-lg p-1 flex flex-col relative" {...cardProps}>
      <CardHeader className="p-2">
        <CardTitle className="text-xs md:text-sm text-center">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-hidden flex-1">
        <Separator className="mb-2" />
        <div className="w-full h-full relative pr-12">
          <ChartContainer
            config={{}}
            className="aspect-square max-h-[350px] w-full"
          >
            <ScatterChart
              width={400}
              height={400}
              margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 20,
              }}
            >
              {showOriginAxis && (
                <CartesianGrid
                  horizontalCoordinatesGenerator={(props) => {
                    const topOffset = props?.offset?.top || 0;
                    return [props.yAxis.height / 2 + topOffset];
                  }}
                  verticalCoordinatesGenerator={(props) => {
                    const leftOffset = props?.offset?.left || 0;
                    return [props.xAxis.width / 2 + leftOffset];
                  }}
                />
              )}
              <XAxis
                type="number"
                dataKey="x"
                ticks={[
                  xDomain[0],
                  xDomain[0] * 0.5,
                  0,
                  xDomain[1] * 0.5,
                  xDomain[1],
                ]}
                domain={xDomain}
                name="Horizontal"
                unit={unit}
                label={{
                  value: horizontalLabel,
                  position: "bottom",
                }}
              />
              <YAxis
                type="number"
                dataKey="y"
                domain={yDomain}
                ticks={[
                  yDomain[0],
                  yDomain[0] * 0.5,
                  0,
                  yDomain[1] * 0.5,
                  yDomain[1],
                ]}
                name="Vertical"
                unit={unit}
                label={{
                  value: verticalLabel,
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Scatter name={title} data={data} fill="#8884d8">
                {colors.map((_entry, index) => (
                  <Cell key={`point-${index}`} fill={colors[index]} />
                ))}
              </Scatter>
            </ScatterChart>
          </ChartContainer>
          {isLegendVisible && (
            <div className="absolute right-0 top-0">
              {datasets.map(({ name, color }) => (
                <div className={`flex items-center space-x-2`} key={name}>
                  <span
                    className={cn(`inline-block w-2 h-2 rounded-full`)}
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                    data-testid={`legend-for-${name}`}
                  />
                  <h3 className="text-xs">{name}</h3>
                </div>
              ))}
            </div>
          )}
          {children}
        </div>
        {loading && (
          <div className="absolute inset-0 bg-white/75 flex items-center justify-center rounded-lg">
            <Spinner className="h-8 w-8" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
